import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, computed } from '@angular/core';
import { Rule } from '../../domain/regs-models';

interface DayItem {
  dow: string;
  dayNum: number;
  open: boolean;
  isToday: boolean;
}

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

@Component({
  selector: 'app-week-strip',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="week-strip">
      @for (d of days(); track d.dayNum) {
        <div class="day-cell" [class.day-cell--today]="d.isToday" [class.day-cell--open]="d.open">
          <span class="day-dow">{{ d.dow }}</span>
          <span class="day-num">{{ d.dayNum }}</span>
          <span class="day-status">{{ d.open ? 'Keep' : 'No' }}</span>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .week-strip {
        display: flex;
        gap: 5px;
        width: 100%;
        margin: 6px 0 10px;
        user-select: none;
      }
      .day-cell {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 8px 0 9px;
        border-radius: 10px;
        background: #fdecee;
      }
      .day-cell--open {
        background: #e3f5ea;
      }
      .day-cell--today {
        border: 2px solid #1c1c1e;
      }
      .day-dow {
        font-size: 12px;
        font-weight: 600;
        color: #c3283a;
      }
      .day-cell--open .day-dow {
        color: #137a43;
      }
      .day-num {
        font-size: 20px;
        font-weight: 700;
        color: #1c1c1e;
        line-height: 1;
        margin: 3px 0;
      }
      .day-status {
        font-size: 11px;
        font-weight: 700;
        color: #c3283a;
      }
      .day-cell--open .day-status {
        color: #137a43;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeekStripComponent {
  @Input({ required: true }) rule!: Rule;
  @Input() today!: Date;
  @Input() set todayDate(d: Date) {
    this.today = d;
  }

  readonly days = computed<DayItem[]>(() => {
    if (!this.rule || !this.today) return [];
    const base = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate());
    const list: DayItem[] = [];

    for (let i = 0; i < 7; i++) {
      const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + i);
      const dayIndex = (d.getDay() + 6) % 7; // Mon = 0, Sun = 6
      const dow = DOW[dayIndex];
      const open = this.isOpenDay(d, dow);
      list.push({
        dow,
        dayNum: d.getDate(),
        open,
        isToday: i === 0,
      });
    }

    return list;
  });

  private isOpenDay(d: Date, dow: string): boolean {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const md = `${mm}-${dd}`;

    for (const n of this.rule.noRetentionDays) {
      const inside =
        n.from <= n.to
          ? md >= n.from && md <= n.to
          : md >= n.from || md <= n.to;
      if (inside && n.weekdays.includes(dow)) {
        return false;
      }
    }
    return true;
  }
}
